import { ApiError } from '@/lib/api';
import type { Note } from '@/lib/types';

/*
 * The rules a note surface shares — the byline, the body ceiling and the
 * refusal sentences — moved out of `NotesPanel.tsx` on 2026-10-16 so the
 * company page's Notes section (`CompanyNotesSection`) reads the SAME copy as
 * the drawer rather than a second one. Nothing here fetches or renders; the
 * two surfaces own their own requests and rows.
 *
 * Moved verbatim: every sentence below is the one the drawer printed before
 * the move, so the drawer's behaviour is unchanged.
 */

/** The server's ceiling. Enforced client-side too so a 422 is not the first feedback. */
export const MAX_BODY = 20_000;

/**
 * The byline on a note EMERGE wrote itself.
 *
 * ⚠️ **`SEED EMERGE`, not `EMERGE`.** The house rule is that every user-facing
 * string writes the full wordmark — code identifiers and file names are the
 * exemption, and a byline is neither.
 *
 * ⛔ **It must never read as a person, and never as "Anonymous".** That word is
 * spoken for one rung away, in the audit log, where it means an
 * *unauthenticated caller* — a third meaning again. Three facts, three words:
 * a name, "Former colleague", and this.
 */
export const SYSTEM_AUTHOR = 'SEED EMERGE';

/**
 * The refusal a write against a system note collects, from `PATCH` and
 * `DELETE` alike.
 *
 * ⚠️ **Matched on `error.code`, never on the message** — the two sentences
 * printed for an ordinary 403 are about an AUTHOR, and a system note has none.
 *
 * ⚠️ **The controls are withheld on a system note and this is still read.** A
 * bundle can be older than the server and a grant can change under a live
 * access token — the hidden control is the optimisation, the refusal is the
 * guarantee.
 */
export const SYSTEM_NOTE_IMMUTABLE = 'SYSTEM_NOTE_IMMUTABLE';

/**
 * The name a note is filed under — the ONE copy of the three-way rule, in the
 * two forms a note surface needs: a LABEL for the row and a clause for the
 * delete dialog. Only the deleted-colleague case differs between them.
 *
 * ⚠️ **Order matters.** `system` is tested FIRST, because a system note ALSO
 * satisfies `author === null` and the second test cannot see past it.
 */
export function bylineOf(note: Note): { label: string; inSentence: string } {
  if (note.system) return { label: SYSTEM_AUTHOR, inSentence: SYSTEM_AUTHOR };
  if (note.author) return { label: note.author.name, inSentence: note.author.name };
  return { label: 'Former colleague', inSentence: 'a former colleague' };
}

export function writeMessage(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 404) return 'That note no longer exists — somebody has deleted it.';
    return cause.fieldErrors.body ?? cause.formMessage ?? cause.message;
  }
  return fallback;
}

/**
 * What a refused EDIT says.
 *
 * TWO 403s, and they route to opposite places — so the code is read BEFORE the
 * status, never after. `SYSTEM_NOTE_IMMUTABLE` means nobody may edit this, the
 * audit grant included; the author sentence means one particular person may.
 * An unrecognised code still falls through to the author sentence
 * deliberately — that is the ordinary refusal on this endpoint. A 403 carries
 * NO `details`, so both render as a block on the row, never against a field.
 */
export function editRefusalMessage(cause: unknown): string {
  if (cause instanceof ApiError && cause.code === SYSTEM_NOTE_IMMUTABLE) {
    return `${SYSTEM_AUTHOR} wrote this note itself, so it cannot be edited by anyone. Add your own note instead.`;
  }
  if (cause instanceof ApiError && cause.status === 403) {
    return 'Only the author can edit a note — the audit grant deliberately does not cover this. Add your own note instead.';
  }
  return writeMessage(cause, 'Could not save this edit. Try again.');
}

/**
 * What a refused DELETE says. Delete is the author OR an audit holder — and the
 * grant is not readable from a note, so a refusal is surfaced rather than
 * pre-empted. This is the half of the pair the audit DOES reach.
 */
export function deleteRefusalMessage(cause: unknown): string {
  if (cause instanceof ApiError && cause.code === SYSTEM_NOTE_IMMUTABLE) {
    return `${SYSTEM_AUTHOR} wrote this note itself, so it cannot be deleted by anyone — the audit grant does not reach it either.`;
  }
  if (cause instanceof ApiError && cause.status === 403) {
    return 'Only the author, or someone with the audit grant, can delete this note.';
  }
  return writeMessage(cause, 'Could not delete this note. Try again.');
}
